import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// ─── Error type ────────────────────────────────────
export class ApiError extends Error {
  public status?: number;
  public code?: number;
  public data?: any;

  constructor(message: string, status?: number, code?: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

// ─── Config interface (backward compat) ────────────
export interface ApiClientConfig {
  baseUrl: string;
  getToken: () => string | null;
  getRefreshToken: () => string | null;
  onTokenRefreshed: (token: string, refreshToken: string) => void;
  onAuthFailure: () => void;
}

export interface ApiClient {
  get<T>(path: string, params?: Record<string, string>): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  delete<T>(path: string): Promise<T>;
  setBaseUrl(url: string): void;
}

// ─── Axios singleton ───────────────────────────────
let instance: AxiosInstance | null = null;
let _config: ApiClientConfig | null = null;

const pendingRequests = new Map<string, AbortController>();

const getRequestKey = (config: AxiosRequestConfig): string => {
  return `${config.method}_${config.url}_${JSON.stringify(config.params)}`;
};

export function createApiClient(config: ApiClientConfig): ApiClient {
  _config = config;

  instance = axios.create({
    baseURL: config.baseUrl,
    timeout: 15000,
    headers: { 'Content-Type': 'application/json' },
  });

  // Request interceptor — inject token + dedup
  instance.interceptors.request.use(
    (cfg) => {
      const token = _config ? _config.getToken() : config.getToken();
      if (token) {
        cfg.headers.Authorization = 'Bearer ' + token;
      }

      // Only deduplicate GET requests
      if (cfg.method && cfg.method.toUpperCase() !== 'GET') {
        return cfg;
      }
      const requestKey = getRequestKey(cfg);
      if (pendingRequests.has(requestKey)) {
        pendingRequests.get(requestKey)?.abort();
      }
      const controller = new AbortController();
      cfg.signal = controller.signal;
      pendingRequests.set(requestKey, controller);

      return cfg;
    },
    (error) => Promise.reject(error),
  );

  // Response interceptor — unwrap envelope + error handling
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const requestKey = getRequestKey(response.config);
      pendingRequests.delete(requestKey);

      const data = response.data;
      if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
        if (data.success === false) {
          throw new ApiError(data.message || '请求失败', response.status, data.code, data.data);
        }
        return data.data as any;
      }
      return data;
    },
    (error: AxiosError) => {
      if (error.config) {
        const requestKey = getRequestKey(error.config);
        pendingRequests.delete(requestKey);
      }

      if (error.name === 'AbortError' || axios.isCancel(error)) {
        return Promise.reject(new Error('请求已取消'));
      }

      if (!error.response) {
        return Promise.reject(new ApiError('网络连接失败，请检查网络', 0));
      }

      const { status } = error.response;
      const data = (error.response.data || {}) as any;
      const msg = data.message || '请求失败';

      if (status === 401) {
        const keys = ['accessToken', 'auth_token', 'token', 'refreshToken', 'zaogig_account', 'zaogig_token', 'zaogig_refresh_token', 'zaogig_user'];
        keys.forEach(k => localStorage.removeItem(k));
        sessionStorage.clear();
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        return Promise.reject(new ApiError('登录已过期，请重新登录', status, data?.code, data?.data));
      }

      return Promise.reject(new ApiError(msg || '请求失败，请重试', status, data?.code, data?.data));
    },
  );

  const api = {
    get: <T>(url: string, cfg?: AxiosRequestConfig) => instance!.get<T, T>(url, cfg),
    post: <T>(url: string, data?: unknown, cfg?: AxiosRequestConfig) => instance!.post<T, T>(url, data, cfg),
    put: <T>(url: string, data?: unknown, cfg?: AxiosRequestConfig) => instance!.put<T, T>(url, data, cfg),
    patch: <T>(url: string, data?: unknown, cfg?: AxiosRequestConfig) => instance!.patch<T, T>(url, data, cfg),
    delete: <T>(url: string, cfg?: AxiosRequestConfig) => instance!.delete<T, T>(url, cfg),
  };

  return {
    get: <T>(path: string, params?: Record<string, string>): Promise<T> => instance!.get<T, T>(path, { params }),
    post: api.post,
    put: api.put,
    patch: api.patch,
    delete: api.delete,
    setBaseUrl(url: string) {
      if (instance) {
        instance.defaults.baseURL = url;
      }
    },
  };
}
